<template>
  <div class="grid">
    <div class="col-12 lg:col-4">
      <Card>
        <template #title>Формирование отчетов аудита</template>
        <template #content>
          <div class="p-fluid">
            <div class="field">
              <label for="reportType">Тип отчета</label>
              <Dropdown
                  id="reportType"
                  v-model="selectedReport"
                  :options="reportOptions"
                  optionLabel="label"
                  placeholder="Выберите тип отчета"
                  class="w-full"
                  :class="{ 'p-invalid': validationErrors.reportType }"
              />
              <small v-if="validationErrors.reportType" class="p-error">{{ validationErrors.reportType }}</small>
            </div>

            <div class="field">
              <label for="cbUuids">ЦБ UUID</label>
              <Textarea
                  id="cbUuids"
                  v-model="cbUuidsInput"
                  :autoResize="true"
                  rows="3"
                  placeholder="Введите список ЦБ UUID через пробел, запятую или точку с запятой"
                  class="w-full"
                  :class="{ 'p-invalid': validationErrors.cbUuids }"
              />
              <small v-if="validationErrors.cbUuids" class="p-error">{{ validationErrors.cbUuids }}</small>
            </div>

            <div class="field-checkbox" v-if="isReportAndFiles">
              <Checkbox
                  id="needFiles"
                  v-model="needFiles"
                  :binary="true"
              />
              <label for="needFiles" class="ml-2">Выгрузить сообщения и квитанции</label>
            </div>

            <div class="field">
              <label class="font-bold">Почта для отправки результата</label>
              <InputText v-model="email" required :disabled="loading"></InputText>
            </div>

            <div class="row" v-if="needFiles">
              <div class="col-12 p-0 mt-2 mb-2">
                <Checkbox
                    binary
                    inputId="splitEventsByFolders"
                    v-model="splitEventsByFolders"
                    :disabled="loading"
                />
                <label class="font-bold ml-1" for="splitEventsByFolders">
                  Разбить события по папкам
                </label>
              </div>
            </div>

            <div class="row" v-if="needFiles">
              <div class="col-12 p-0 mt-2 mb-2">
                <Checkbox
                    binary
                    inputId="exportAsSingleFile"
                    v-model="exportAsSingleFile"
                    :disabled="loading"
                />
                <label class="font-bold ml-1" for="exportAsSingleFile">
                  Выгрузка данных одним файлом (кроме удаления КИ)
                </label>
              </div>
            </div>

            <div class="field">
              <Button
                  :loading="loading"
                  @click="generateReport"
                  label="Сформировать"
                  icon="pi pi-file"
                  class="w-full"
              />
            </div>

            <div v-if="validUuids.length > 0" class="mt-3">
              <Message severity="info" :closable="false">
                <div class="font-bold mb-2">Найдено валидных UUID: {{ validUuids.length }}</div>
                <div class="text-sm text-color-secondary" style="max-height: 100px; overflow-y: auto;">
                  {{ validUuids.join(', ') }}
                </div>
              </Message>
            </div>

            <div v-if="invalidUuids.length > 0" class="mt-3">
              <Message severity="warn" :closable="false">
                <div class="font-bold mb-2">Обнаружены некорректные UUID ({{ invalidUuids.length }}):</div>
                <ul class="m-0 pl-4" style="max-height: 100px; overflow-y: auto;">
                  <li v-for="(uuid, index) in invalidUuids" :key="index" class="text-sm">
                    "{{ uuid }}" - не соответствует формату 8-4-4-4-12-1
                  </li>
                </ul>
                <div class="mt-2 text-sm font-bold">
                  Они будут пропущены при формировании отчета
                </div>
              </Message>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import Dropdown from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ResponseService from "@/service/ResponseService";
import FunctionsService from "@/service/FunctionsService";

export default {
  name: "Reports",
  components: {
    Dropdown,
    Textarea,
    Checkbox,
    Button,
    Message
  },
  data() {
    return {
      loading: false,
      selectedReport: null,
      cbUuidsInput: '',
      needFiles: false,
      email: null,
      splitEventsByFolders: false,
      exportAsSingleFile: false,
      validationErrors: {
        reportType: null,
        cbUuids: null
      },
      reportOptions: [
        { label: 'События и файлы выгрузок', value: 'unloading' },
        { label: 'Аудит ВСП', value: 'vsp' }
      ]
    }
  },
  computed: {
    isReportAndFiles() {
      return this.selectedReport?.value === 'unloading';
    },

    parsedUuids() {
      if (!this.cbUuidsInput.trim()) return [];

      return this.cbUuidsInput
          .split(/[,\s;]+/)
          .map(item => item.trim())
          .filter(item => item.length > 0);
    },

    uuidRegex() {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-[0-9a-f]+$/i;
    },

    validUuids() {
      return this.parsedUuids.filter(uuid => this.uuidRegex.test(uuid));
    },

    invalidUuids() {
      return this.parsedUuids.filter(uuid => !this.uuidRegex.test(uuid));
    },

    hasValidUuids() {
      return this.validUuids.length > 0;
    },

    uniqueValidUuids() {
      return [...new Set(this.validUuids)];
    }
  },
  watch: {

    splitEventsByFolders(val) {
      if (val) this.exportAsSingleFile = false
    },
    exportAsSingleFile(val) {
      if (val) this.splitEventsByFolders = false
    },

    selectedReport(newVal) {
      if (newVal?.value !== 'unloading') {
        this.needFiles = false;
      }
      this.validationErrors.reportType = null;
    },

    cbUuidsInput() {
      this.validationErrors.cbUuids = null;
    }
  },
  methods: {
    validateForm() {
      let isValid = true;
      this.validationErrors = { reportType: null, cbUuids: null };

      if (!this.selectedReport) {
        this.validationErrors.reportType = 'Выберите тип отчета';
        isValid = false;
      }

      if (!this.cbUuidsInput.trim()) {
        this.validationErrors.cbUuids = 'Введите хотя бы один ЦБ UUID';
        isValid = false;
      } else if (!this.hasValidUuids) {
        this.validationErrors.cbUuids = 'Нет ни одного корректного UUID';
        isValid = false;
      }

      if (!this.email.trim()) {
        this.validationErrors.cbUuids = 'Укажите email для отправки результата';
        isValid = false;
      }

      return isValid;
    },

    generateReport() {
      if (!this.validateForm()) {
        const firstError = Object.values(this.validationErrors).find(error => error);
        if (firstError) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Ошибка валидации',
            detail: firstError,
            life: 3000
          });
        }
        return;
      }

      const uniqueUuids = this.uniqueValidUuids;
      const hasDuplicates = uniqueUuids.length < this.validUuids.length;

      let confirmMessage = `Вы уверены, что хотите сформировать отчет "${this.selectedReport.label}"?\n\n`;
      confirmMessage += `Будут обработаны (${uniqueUuids.length} шт. UUID):\n`;

      if (hasDuplicates) {
        confirmMessage += `\n⚠️ Обнаружены дубликаты, они будут обработаны один раз\n`;
      }

      if (this.invalidUuids.length > 0) {
        confirmMessage += `\n❌ Следующие значения будут пропущены (${this.invalidUuids.length} шт.):\n`;
        this.invalidUuids.forEach(uuid => {
          confirmMessage += `• "${uuid}" (некорректный формат)\n`;
        });
        confirmMessage += `\nФормат UUID: 8-4-4-4-12-1 (например: 3f5ea006-202c-1627-8be9-2a8ab13a5003-8)\n`;
      }

      if (this.isReportAndFiles && this.needFiles) {
        confirmMessage += `\n📎 Будут выгружены сообщения и квитанции для каждого UUID.`;
      }

      this.$confirm.require({
        message: confirmMessage,
        header: 'Подтвердите формирование отчета',
        acceptLabel: 'Сформировать',
        acceptIcon: 'pi pi-file',
        rejectLabel: 'Отмена',
        rejectIcon: 'pi pi-ban',
        accept: () => {
          this.$confirm.close();
          this.loading = true;

          const reportType = this.selectedReport.value;

          FunctionsService.getAudit(uniqueUuids, reportType, this.email, this.needFiles, this.splitEventsByFolders, this.exportAsSingleFile)
              .then((response) => {
                if (response.error) {
                  this.$toast.add({
                    severity: 'error',
                    summary: 'Отчет не сформирован',
                    detail: response.feedback || 'Произошла ошибка при формировании отчета',
                    life: 5000
                  });
                } else {
                  let detailMessage = `Отчет "${this.selectedReport.label}" успешно сформирован для ${uniqueUuids.length} UUID`;

                  if (this.isReportAndFiles && this.needFiles) {
                    detailMessage += ` с выгрузкой сообщений и квитанций`;
                  }

                  this.$toast.add({
                    severity: 'success',
                    summary: 'Отчет сформирован',
                    detail: detailMessage,
                    life: 5000
                  });

                  // Очищаем форму
                  this.selectedReport = null;
                  this.cbUuidsInput = '';
                  this.needFiles = false;
                }
                this.loading = false;
              })
              .catch((err) => {
                ResponseService.handleErrorResponseWithoutForm(err, this.$toast);
                this.loading = false;
              });
        }
      });
    }
  }
}
</script>

<style scoped>
.field {
  margin-bottom: 1.5rem;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.field-checkbox {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
}

.field-checkbox label {
  margin-left: 0.5rem;
  cursor: pointer;
}

.p-error {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.text-color-secondary {
  color: var(--text-color-secondary);
}

ul {
  list-style-type: none;
  padding-left: 0;
}

li {
  font-family: monospace;
  margin-bottom: 0.25rem;
  word-break: break-all;
}
</style>